<!-- Add/Edit address — profile redesign. All logic is unchanged: reactive
     form + validators + postalCode directive, confirmation dialog,
     create/update service calls, back-navigation. Presentation only,
     mirroring the Information panel's field treatment. -->
<section class="vb-addr-form">
    <vb-panel-header
        [title]="isEditMode ? 'Edit address' : 'Add address'"></vb-panel-header>

    <form class="vb-addr-form__card" id="addressForm" [formGroup]="addressForm">
        <div class="vb-addr-form__field">
            <label class="vb-addr-form__label" for="name">Contact name</label>
            <input
                class="vb-addr-form__input"
                name="name"
                id="name"
                type="text"
                placeholder="Enter contact name..."
                autocomplete="name"
                formControlName="name" />
            <span class="vb-addr-form__error" *ngIf="getHelperText('name')">
                {{ getHelperText("name") }}
            </span>
        </div>

        <div class="vb-addr-form__field">
            <label class="vb-addr-form__label" for="country">Country</label>
            <span class="vb-addr-form__select-wrap">
                <select
                    class="vb-addr-form__input vb-addr-form__select"
                    id="country"
                    formControlName="country"
                    (change)="onCountrySelected()">
                    <option value="" disabled selected>
                        Select country...
                    </option>
                    <option
                        *ngFor="let country of countries"
                        [value]="country.value">
                        {{ country.viewValue }}
                    </option>
                </select>
                <vb-icon
                    class="vb-addr-form__select-chev"
                    name="chevDown"
                    [size]="16"></vb-icon>
            </span>
            <span class="vb-addr-form__error" *ngIf="getHelperText('country')">
                {{ getHelperText("country") }}
            </span>
        </div>

        <div class="vb-addr-form__field">
            <label class="vb-addr-form__label" for="address1">Address</label>
            <input
                class="vb-addr-form__input"
                name="address1"
                id="address1"
                type="text"
                placeholder="Street address or P.O. Box"
                autocomplete="address-line1"
                formControlName="address1" />
            <span class="vb-addr-form__error" *ngIf="getHelperText('address1')">
                {{ getHelperText("address1") }}
            </span>
        </div>

        <div class="vb-addr-form__field">
            <label class="vb-addr-form__label" for="address2">
                Address line 2
            </label>
            <input
                class="vb-addr-form__input"
                name="address2"
                id="address2"
                type="text"
                placeholder="Apt, suite, unit, building"
                autocomplete="address-line2"
                formControlName="address2" />
            <span class="vb-addr-form__error" *ngIf="getHelperText('address2')">
                {{ getHelperText("address2") }}
            </span>
        </div>

        <div class="vb-addr-form__grid">
            <div class="vb-addr-form__field">
                <label class="vb-addr-form__label" for="city">City</label>
                <input
                    class="vb-addr-form__input"
                    name="city"
                    id="city"
                    type="text"
                    placeholder="Enter city..."
                    autocomplete="address-level2"
                    formControlName="city" />
                <span class="vb-addr-form__error" *ngIf="getHelperText('city')">
                    {{ getHelperText("city") }}
                </span>
            </div>
            <div class="vb-addr-form__field">
                <label class="vb-addr-form__label" for="province">
                    {{ provinceLabel }}
                </label>
                <span class="vb-addr-form__select-wrap">
                    <select
                        class="vb-addr-form__input vb-addr-form__select"
                        id="province"
                        formControlName="province">
                        <option value="" disabled selected>Select...</option>
                        <option
                            *ngFor="let province of filteredProvinces"
                            [value]="province.value">
                            {{ province.viewValue }}
                        </option>
                    </select>
                    <vb-icon
                        class="vb-addr-form__select-chev"
                        name="chevDown"
                        [size]="16"></vb-icon>
                </span>
                <span
                    class="vb-addr-form__error"
                    *ngIf="getHelperText('province')">
                    {{ getHelperText("province") }}
                </span>
            </div>
        </div>

        <div class="vb-addr-form__grid">
            <div class="vb-addr-form__field">
                <label class="vb-addr-form__label" for="postalCode">
                    {{ postalCodeLabel }}
                </label>
                <input
                    class="vb-addr-form__input vb-addr-form__input--mono"
                    name="postalCode"
                    id="postalCode"
                    type="text"
                    [placeholder]="postalCodePlaceholder"
                    autocomplete="postal-code"
                    maxlength="10"
                    required
                    postalCode
                    formControlName="postalCode" />
                <span
                    class="vb-addr-form__error"
                    *ngIf="getHelperText('postalCode')">
                    {{ getHelperText("postalCode") }}
                </span>
            </div>
            <div class="vb-addr-form__field">
                <label class="vb-addr-form__label" for="buzzer">
                    Buzzer/door code
                </label>
                <input
                    class="vb-addr-form__input vb-addr-form__input--mono"
                    name="buzzer"
                    id="buzzer"
                    type="text"
                    placeholder="XXXXX"
                    formControlName="buzzer" />
                <span
                    class="vb-addr-form__error"
                    *ngIf="getHelperText('buzzer')">
                    {{ getHelperText("buzzer") }}
                </span>
            </div>
        </div>

        <div class="vb-addr-form__default">
            <vb-checkbox
                label="Make this your default address"
                [checked]="!!isDefault?.value"
                (checkedChange)="isDefault?.setValue($event)"></vb-checkbox>
        </div>

        <div class="vb-addr-form__actions">
            <vb-button variant="secondary" (clicked)="goToListAddress()">
                Cancel
            </vb-button>
            <vb-button
                variant="primary"
                [disabled]="addressForm.invalid"
                (clicked)="openConfirmationModal()">
                {{ isEditMode ? "Save changes" : "Add address" }}
            </vb-button>
        </div>
    </form>

    <!-- Save confirm — vb-dialog with the same translated copy the legacy
         ngx-bootstrap confirmation showed. -->
    <vb-dialog
        [open]="confirmOpen"
        [title]="confirmTitle"
        (closed)="confirmOpen = false">
        <p class="vb-addr-form__confirm-copy">{{ confirmMessage }}</p>
        <!-- vb-dialog's footer slot stacks children (its full-width recipe);
             a single row wrapper keeps Cancel/Confirm side by side. -->
        <div class="vb-addr-form__confirm-actions" dialogFooter>
            <vb-button
                variant="ghost"
                [fullWidth]="true"
                (clicked)="confirmOpen = false">
                Cancel
            </vb-button>
            <vb-button
                variant="primary"
                [fullWidth]="true"
                (clicked)="onSaveConfirmed()">
                Confirm
            </vb-button>
        </div>
    </vb-dialog>
</section>
